import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [profile, setProfile]         = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await Promise.all([
          fetchProfile(session.user.id),
          fetchSubscription(session.user.id),
        ]);
      }
      setLoading(false);
    });

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
          fetchSubscription(session.user.id);
        } else {
          setProfile(null);
          setSubscription(null);
        }
      }
    );

    return () => authSub.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles').select('*').eq('id', userId).single();
    setProfile(data);
  }

  async function fetchSubscription(userId) {
    const { data } = await supabase
      .from('subscriptions')
      .select('plan, status, current_period_end, trial_ends_at, stripe_customer_id')
      .eq('user_id', userId)
      .single();
    setSubscription(data || { plan: 'trial', status: null });
  }

  async function refreshSubscription() {
    if (!user) return;
    await fetchSubscription(user.id);
  }

  async function refreshProfile() {
    if (!user) return;
    await fetchProfile(user.id);
  }

  async function signUp({ email, password, fullName, companyName, role = 'vermieter' }) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        data: { full_name: fullName, company_name: companyName, role },
        emailRedirectTo: `${window.location.origin}/dashboard`,
      }
    });
    if (!error && data.user) {
      await supabase.from('profiles').update({ company_name: companyName, role }).eq('id', data.user.id);
    }
    return { data, error };
  }

  async function signIn({ email, password }) {
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.data?.user) {
      setUser(result.data.user);
      fetchProfile(result.data.user.id);
      fetchSubscription(result.data.user.id);
    }
    return result;
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ user, profile, subscription, loading, signUp, signIn, signOut, refreshSubscription, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
